import cn from 'classnames'
import {useEffect, useRef, useState} from 'react'
import {Checkbox, Col, ConfigProvider, Radio} from 'src/ui'
import style from './index.module.scss'

interface TreeProps {
  data: any[] // 数据类型{label: '', value: '', children: []}
  disabled?: boolean // 是否能编辑
  themeColor?: string // 主题色
  labelName?: string // label的字段key 默认字段label
  valueName?: string // value值的字段key 默认字段value
  radioKey?: string // 单选的字段标识key 默认字段type
  radioValue?: string | number // 单选字段value  默认为'radio'
  defaultSelectedKeys?: any[] // 默认选择的节点值
  onChange?: (keys: any[]) => void
}
export const Tree = (props: TreeProps) => {
  const {
    data,
    disabled = false,
    labelName = 'label',
    valueName = 'value',
    themeColor = '#FF9400',
    radioKey = 'type',
    radioValue = 'radio',
    defaultSelectedKeys = [],
  } = props
  const [indeterminateKeys, setIndeterminateKeys] = useState<any[]>([]) //子节点部分选择的父节点value
  const [selectKeys, setSelectKeys] = useState<any[]>([]) //子节点全选的父节点value
  const [treeOb, setTreeOb] = useState<any>({}) //树状数组转的对象
  const selectKeysRef: any = useRef(null)
  const indeterminateKeysRef: any = useRef(null)

  useEffect(() => {
    init()
  }, [props.data, props.defaultSelectedKeys])

  const handleIndeterminate = (data: any) => {
    const newIndeterminateKeys = indeterminateKeysRef.current.filter((item: any) => item !== data[valueName])
    indeterminateKeysRef.current = newIndeterminateKeys
  }
  const handleChange = (data: any, parentKey?: string) => {
    handleIndeterminate(data)
    // 原来是选中
    if (selectKeysRef.current.indexOf(data[valueName]) > -1) {
      const newSelectKeys = selectKeysRef.current.filter((item: any) => item !== data[valueName])
      selectKeysRef.current = newSelectKeys
    } else {
      selectKeysRef.current.push(data[valueName])
    }
    if (parentKey) {
      changeParentStatus(parentKey)
    }
    changeChildStatus(data)
    setKeys()
    props.onChange && props.onChange(selectKeysRef.current)
  }

  // radio节点数据change
  // 增加当前选中的节点,并删除同一层级下旧的选择节点
  const handleRadioChange = (e: any, parentKey: string) => {
    const value = JSON.parse(e.target.value)
    const currentChildren = treeOb[parentKey].children
    //当前选择radio是没有选中的
    if (selectKeysRef.current.indexOf(value[valueName]) === -1) {
      let newSelectKeys: any = selectKeysRef.current
      const selectChild = currentChildren.filter((item: any) => selectKeysRef.current.indexOf(item[valueName]) > -1) //当前层级下 是否有其他选中的radio
      if (selectChild && selectChild.length > 0) {
        // 删除旧的选择radio
        newSelectKeys = selectKeysRef.current.filter((item: any) => item !== selectChild[0][valueName])
      }
      //增加当前选中的radio
      newSelectKeys.push(value[valueName])
      selectKeysRef.current = newSelectKeys
    }
    if (parentKey) changeParentStatus(parentKey)
    setKeys()
    props.onChange && props.onChange(selectKeysRef.current)
  }
  const setKeys = () => {
    const keys = JSON.parse(JSON.stringify(selectKeysRef.current))
    const newIndeterminateKeys = JSON.parse(JSON.stringify(indeterminateKeysRef.current))
    setSelectKeys(keys)
    setIndeterminateKeys(newIndeterminateKeys)
  }
  const init = () => {
    selectKeysRef.current = JSON.parse(JSON.stringify(defaultSelectedKeys))
    indeterminateKeysRef.current = []
    const treeObject = transformTreeArrayToOb(data)
    setTreeOb(treeObject)
    setKeys()
  }
  // 将树状数组每个节点转为对象
  const transformTreeArrayToOb = (tree: any) => {
    const result: any = {}
    const traverse = (node: any, parentKey?: string) => {
      if (parentKey) node.parentKey = parentKey
      result[node[valueName]] = node // 将当前节点对象放入结果对象
      // 如果有子节点，递归遍历
      if (node.children && node.children.length > 0) {
        node.children.forEach((child: any) => traverse(child, node[valueName]))
        // 判断当前节点是否部分选择
        const filterChild = node.children.filter((item: any) => selectKeysRef.current.indexOf(item[valueName]) > -1)
        if (
          filterChild.length > 0 &&
          filterChild.length < node.children.length &&
          node.children[0][radioKey] !== radioValue
        ) {
          indeterminateKeysRef.current.push(node[valueName])
        }
      }
    }
    tree.forEach((node: any) => traverse(node))
    return result
  }
  const changeChildStatus = (currentInfo: any) => {
    // 遍历子节点状态
    const children = currentInfo.children || []
    const selectedChildren = children.filter((item: any) => selectKeysRef.current.indexOf(item[valueName]) > -1)

    // 当前组件状态变化是，子节点为全部选中或者全部不选中，两种状态都需要删除子节点所有indeterminate
    children.map((child: any) => {
      const newIndeterminateKeys = indeterminateKeysRef.current.filter((item: any) => item !== child[valueName])
      indeterminateKeysRef.current = newIndeterminateKeys
    })

    // 子节点全选
    if (selectKeysRef.current.indexOf(currentInfo[valueName]) > -1 && selectedChildren.length < children.length) {
      if (children && children[0][radioKey] !== radioValue) {
        children.map((child: any) => {
          if (selectKeysRef.current.indexOf(child[valueName]) === -1) {
            selectKeysRef.current.push(child[valueName])
            changeChildStatus(child)
          }
        })
      } else {
        // 子节点为radio 只选择第一个
        if (selectKeysRef.current.indexOf(children[0][valueName]) === -1) {
          selectKeysRef.current.push(children[0][valueName])
        }
      }
    }
    // 子节点全去除
    if (selectKeysRef.current.indexOf(currentInfo[valueName]) === -1 && selectedChildren.length > 0) {
      children.map((child: any) => {
        const newSelectKeys = selectKeysRef.current.filter((item: any) => item !== child[valueName])
        selectKeysRef.current = newSelectKeys
        changeChildStatus(child)
      })
    }
  }

  // 遍历更新parent节点状态
  const changeParentStatus = (parentKey: string) => {
    const parentInfo = treeOb[parentKey]
    const selectedChildren = parentInfo.children.filter(
      (item: any) => selectKeysRef.current.indexOf(item[valueName]) > -1,
    )
    // 增加节点selected
    if (
      selectedChildren &&
      selectedChildren.length > 0 &&
      selectKeysRef.current.indexOf(parentInfo[valueName]) === -1
    ) {
      selectKeysRef.current.push(parentInfo[valueName])
    }
    // 增加节点indeterminate
    if (
      selectedChildren &&
      selectedChildren.length > 0 &&
      selectedChildren.length < parentInfo.children.length &&
      parentInfo.children[0][radioKey] !== radioValue
    ) {
      if (indeterminateKeysRef.current.indexOf(parentInfo[valueName]) === -1) {
        indeterminateKeysRef.current.push(parentInfo[valueName])
      }
    } else {
      // 删除节点indeterminate
      const newIndeterminateKeys = indeterminateKeysRef.current.filter((item: any) => item !== parentInfo[valueName])
      indeterminateKeysRef.current = newIndeterminateKeys
    }
    // 删除节点selected
    if (
      selectedChildren &&
      selectedChildren.length === 0 &&
      selectKeysRef.current.indexOf(parentInfo[valueName]) > -1
    ) {
      const newSelectKeys = selectKeysRef.current.filter((item: any) => item !== parentInfo[valueName])
      selectKeysRef.current = newSelectKeys
    }
    if (parentInfo.parentKey) changeParentStatus(parentInfo.parentKey)
  }
  const getRadioGroupValue = (data: any) => {
    let val = ''
    data.some((item: any) => {
      if (selectKeys.indexOf(item[valueName]) > -1) {
        val = JSON.stringify(item)
      }
      return selectKeys.indexOf(item[valueName]) > -1
    })
    return val
  }
  const checkIfIndeterminate = (item: {[x: string]: any}) => {
    return indeterminateKeys.indexOf(item[valueName]) > -1
  }
  const checkIfSelected = (item: {[x: string]: any}) => {
    return selectKeys.indexOf(item[valueName]) > -1
  }
  const renderTree = (data: any[], parentKey = '', level = '0') => {
    if (data && data.length > 0) {
      if (data[0][radioKey] !== radioValue) {
        return (
          <div className={cn(style.wrapClass, level === '1' ? style.nopadding : '')}>
            {data.map((item, index) => {
              return (
                <Col key={index} className={cn(level === '1' ? style.highlight : '')}>
                  <Checkbox
                    indeterminate={checkIfIndeterminate(item)}
                    disabled={disabled}
                    checked={checkIfSelected(item)}
                    className={style.item}
                    onChange={() => {
                      handleChange(item, parentKey)
                    }}
                  >
                    {item[labelName]}
                  </Checkbox>
                  {renderTree(item.children, item[valueName])}
                </Col>
              )
            })}
          </div>
        )
      } else {
        return (
          <div className={style.wrapClass}>
            <Radio.Group
              onChange={(e: any) => {
                handleRadioChange(e, parentKey)
              }}
              disabled={disabled}
              value={getRadioGroupValue(data)}
            >
              {data.map((item, index) => {
                return (
                  <Radio value={JSON.stringify(item)} key={index} name="radiogroup" className={style.item}>
                    {item[labelName]}
                  </Radio>
                )
              })}
            </Radio.Group>
          </div>
        )
      }
    } else {
      return <></>
    }
  }
  return (
    <ConfigProvider
      theme={{
        token: {
          // Seed Token，影响范围大
          colorPrimary: themeColor,
          // 派生变量，影响范围小
          colorBgContainer: '#fff',
        },
      }}
    >
      {renderTree(data, '', '1')}
    </ConfigProvider>
  )
}
export default Tree
